import type { ReactElement, ReactNode } from "react";

import { cn } from "@/lib/utils";

export type NoticeTone = "success" | "error" | "info";

const TONES: Record<NoticeTone, string> = {
  success: "bg-success-soft text-success",
  error: "bg-destructive/5 text-destructive",
  info: "bg-surface-sunken text-foreground",
};

/**
 * The answer to a click, next to where it happened. Success and failure look different
 * (until 05.10.2026 both were the same blue rule). An error is an alert, everything else a
 * polite status, so a screen reader hears it once.
 */
export function Notice({
  tone,
  children,
  className,
}: {
  readonly tone: NoticeTone;
  readonly children: ReactNode;
  readonly className?: string;
}): ReactElement {
  return (
    <div
      className={cn("rounded-md px-4 py-3 text-sm", TONES[tone], className)}
      role={tone === "error" ? "alert" : "status"}
      aria-live={tone === "error" ? undefined : "polite"}
    >
      {children}
    </div>
  );
}

/** The outcome of a write as most pages hold it: `ok` plus the server's sentence. */
export function OutcomeNotice({
  outcome,
  className,
}: {
  readonly outcome: { readonly ok: boolean; readonly message?: string | null };
  readonly className?: string;
}): ReactElement {
  return (
    <Notice tone={outcome.ok ? "success" : "error"} className={className}>
      {outcome.message ?? (outcome.ok ? "Erledigt." : "Das hat nicht geklappt.")}
    </Notice>
  );
}
